import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  StatCard,
  Table,
  Thead,
  Th,
  Td,
  TRow,
  Button,
  Select,
  Input,
  Badge,
  EmptyState,
  Muted,
  AlertBanner,
  TabGroup,
} from "../ui";
import PlcbInvoiceFields, {
  type PlcbInvoiceForm,
  type PlcbInvoiceRecord,
  blankPlcbForm,
  plcbFormFrom,
  plcbMath,
  plcbPayload,
} from "./PlcbInvoiceFields";

// ============================================================
// TYPES
// ============================================================

interface PO {
  id: string;
  vendor_id: string;
  vendor_name: string;
  vendor_order_email: string;
  sales_rep_name: string | null;
  sales_rep_email: string | null;
  status: string;
  total_amount: number;
  invoice_amount: number | null;
  invoice_image_path: string | null;
  has_discrepancies: boolean;
  created_from: string;
  notes: string | null;
  created_at: string;
  submitted_at: string | null;
  received_at: string | null;
  submitted_by_name: string | null;
  received_by_name: string | null;
  expected_delivery_date: string | null;
  lines: POLine[];
  receiving_note?: string | null;
  send_outcome?: string | null;
  sent_to_vendor_at?: string | null;
  sent_to_vendor_email?: string | null;
  delivery_fee?: number | null;
  keg_deposit_charged?: number | null;
  keg_deposit_returned?: number | null;
  plcb_invoice?: PlcbInvoiceRecord | null;
}

interface POLine {
  id: string;
  product_id: string;
  product_name: string;
  brand: string;
  case_configuration: string;
  receive_format_name: string;
  qty_ordered: number;
  qty_received: number;
  qty_damaged: number;
  unit_cost: number;
  line_total: number;
  cases_ordered: number;
  cost_per_case: number;
  oz_per_unit: number;
  oz_per_case: number;
  sold_2wk: number;
  sold_2wk_cases: number;
  cases_on_hand: number;
  substitution_note: string | null;
  invoice_qty: number | null;
  invoice_cost: number | null;
  discrepancy_type: string | null;
  discrepancy_note: string | null;
}

interface POListItem {
  id: string;
  vendor_name: string;
  status: string;
  total_amount: number;
  invoice_amount: number | null;
  created_at: string;
  submitted_at: string | null;
  submitted_by_name: string | null;
  received_by_name: string | null;
  expected_delivery_date: string | null;
}

interface Vendor {
  id: string;
  name: string;
}

interface Suggestion {
  case_sku?: string;
  wholesaler_oos_since?: string | null;
  product_id: string;
  product_name: string;
  brand: string;
  case_configuration: string;
  oz_on_hand: number;
  oz_per_case: number;
  cases_on_hand: number;
  receive_format_id: string;
  receive_format_name: string;
  oz_per_unit: number;
  default_cost: number;
  suggested_qty: number;
  below_par: boolean;
  par_min_oz: number;
  par_max_oz: number;
  sold_2wk: number;
  sold_2wk_cases: number;
  days_sold_last_2wk: number;
  weekly_avg_qty: number;
  days_of_stock: number;
}

interface VendorCalendarEntry {
  id: string;
  name: string;
  day_of_week: number;
  entry_type: string;
  cutoff_time: string | null;
}

// ============================================================
// HELPERS
// ============================================================

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const STATUS_BADGE: Record<string, "default" | "blue" | "green" | "danger" | "gold"> = {
  draft: "default",
  submitted: "blue",
  received: "green",
  cancelled: "danger",
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// ============================================================
// MAIN COMPONENT
// ============================================================

type View = "list" | "detail";

export default function PurchaseOrdersPage() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";

  const [view, setView] = useState<View>("list");
  const [selectedPoId, setSelectedPoId] = useState<string | null>(null);

  const openDetail = useCallback((poId: string) => {
    setSelectedPoId(poId);
    setView("detail");
  }, []);

  const backToList = useCallback(() => {
    setView("list");
    setSelectedPoId(null);
  }, []);

  if (view === "detail" && selectedPoId) {
    return <PODetailView poId={selectedPoId} onBack={backToList} isManager={isManager} />;
  }

  return <POListView onOpenPO={openDetail} isManager={isManager} />;
}

// ============================================================
// LIST VIEW
// ============================================================

function POListView({
  onOpenPO,
  isManager,
}: {
  onOpenPO: (id: string) => void;
  isManager: boolean;
}) {
  const [pos, setPos] = useState<POListItem[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [calendar, setCalendar] = useState<VendorCalendarEntry[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // One week at a time, Patrick's-sheet style. Weeks run Sun-Sat like the
  // budget page; a PO belongs to the week of its delivery date.
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const d = new Date(); d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay());
    return d;
  });

  useEffect(() => {
    loadData();
  }, [statusFilter, vendorFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (vendorFilter) params.set("vendor_id", vendorFilter);

      const [posRes, vendorRes, calRes] = await Promise.all([
        api.get(`/purchase-orders?${params}`),
        api.get("/vendors"),
        api.get("/vendors/calendar").catch(() => ({ data: [] })),
      ]);
      setPos(posRes.data);
      setVendors(vendorRes.data);
      setCalendar(calRes.data);
    } catch {
      setError("Could not load purchase orders.");
    } finally {
      setLoading(false);
    }
  };

  const createPO = async (vendorId: string) => {
    try {
      const response = await api.post("/purchase-orders", { vendor_id: vendorId });
      onOpenPO(response.data.id);
    } catch {
      setError("Failed to create purchase order.");
    }
  };

  // Week bucketing: delivery date first, else created date
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 7);
  const poWeekDate = (p: POListItem) => new Date((p.expected_delivery_date || p.created_at).slice(0, 10) + "T12:00:00");
  const weekPos = pos.filter((p) => { const d = poWeekDate(p); return d >= weekStart && d < weekEnd; });
  const isCurrentWeek = (() => { const n = new Date(); n.setHours(12, 0, 0, 0); n.setDate(n.getDate() - n.getDay()); return n.getTime() === weekStart.getTime(); })();
  const shiftWeek = (n: number) => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() + n * 7); return d; });
  const fmtWk = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const lastDay = new Date(weekEnd); lastDay.setDate(lastDay.getDate() - 1);

  // Summary stats (this week only; closed strays don't count toward the total)
  const draftCount = weekPos.filter((p) => p.status === "draft").length;
  const submittedCount = weekPos.filter((p) => p.status === "submitted").length;
  const totalValue = weekPos.filter((p) => p.status !== "closed").reduce((sum, p) => sum + Number(p.total_amount), 0);

  // Today's schedule
  const todayDow = new Date().getDay();
  const todaySchedule = calendar.filter((c) => c.day_of_week === todayDow);

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Purchase Orders" subtitle="Loading..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Purchase Orders"
        subtitle={`${weekPos.length} order${weekPos.length !== 1 ? "s" : ""} this week`}
        action={
          isManager ? (
            <div className="flex items-center gap-2">
              <Select value="" onChange={(val) => { if (val) createPO(val); }}>
                <option value="">+ New Order...</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </Select>
            </div>
          ) : undefined
        }
      />

      {error && <AlertBanner variant="danger">{error}</AlertBanner>}

      {/* Today's schedule banner */}
      {todaySchedule.length > 0 && (
        <AlertBanner variant="gold">
          <h2 className="font-bold mb-2">Today - {DAY_NAMES[todayDow]}</h2>
          <div className="flex flex-wrap gap-3">
            {todaySchedule.map((entry, i) => (
              <div
                key={i}
                className="rounded-lg px-4 py-2 flex items-center gap-3"
                style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
              >
                <div>
                  <span className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                    {entry.name}
                  </span>
                  <Muted>
                    {" "}{entry.entry_type === "order_due" ? "Order Due" : "Delivery"}
                    {entry.cutoff_time ? ` by ${entry.cutoff_time}` : ""}
                  </Muted>
                </div>
                {entry.entry_type === "order_due" && isManager && (
                  <Button size="sm" onClick={() => createPO(entry.id)}>Create Order</Button>
                )}
              </div>
            ))}
          </div>
        </AlertBanner>
      )}

      {/* Week navigation */}
      <div className="flex items-center gap-3 mb-4">
        <Button size="sm" variant="ghost" onClick={() => shiftWeek(-1)}>‹ Prev</Button>
        <div className="font-bold text-lg" style={{ color: "var(--rbw-navy)" }}>
          Week of {fmtWk(weekStart)} – {fmtWk(lastDay)}{isCurrentWeek ? " (this week)" : ""}
        </div>
        <Button size="sm" variant="ghost" onClick={() => shiftWeek(1)}>Next ›</Button>
        {!isCurrentWeek && (
          <Button size="sm" onClick={() => { const d = new Date(); d.setHours(12, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); setWeekStart(d); }}>
            This Week
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Draft" value={String(draftCount)} variant="default" />
        <StatCard label="Submitted" value={String(submittedCount)} variant="blue" />
        <StatCard label="Week Total" value={fmt(totalValue)} variant="gold" />
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <Select value={statusFilter} onChange={setStatusFilter}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </Select>
        <Select value={vendorFilter} onChange={setVendorFilter}>
          <option value="">All Vendors</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </Select>
      </div>

      {/* PO Table */}
      {weekPos.length === 0 ? (
        <EmptyState message="No purchase orders this week" detail="Use Prev/Next to see other weeks, or create a new order" icon="📋" />
      ) : (
        <Table>
          <Thead>
            <Th>Vendor</Th>
            <Th>Status</Th>
            <Th>Delivery</Th>
            <Th align="right">Total</Th>
            <Th align="right">Invoice</Th>
            <Th>Created</Th>
            <Th>By</Th>
            <Th align="center">Actions</Th>
          </Thead>
          <tbody>
            {weekPos.map((po, idx) => (
              <TRow key={po.id} index={idx}>
                <Td bold>
                  <button
                    onClick={() => onOpenPO(po.id)}
                    className="hover:underline text-left font-semibold"
                    style={{ color: "var(--rbw-navy)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  >
                    {po.vendor_name}
                  </button>
                </Td>
                <Td>
                  <Badge variant={STATUS_BADGE[po.status] || "default"}>
                    {po.status}
                  </Badge>
                </Td>
                <Td>{po.expected_delivery_date ? fmtDate(po.expected_delivery_date) : <Muted>--</Muted>}</Td>
                <Td align="right" bold>{fmt(Number(po.total_amount))}</Td>
                <Td align="right">
                  {po.invoice_amount ? fmt(Number(po.invoice_amount)) : <Muted>--</Muted>}
                </Td>
                <Td>{fmtDate(po.created_at)}</Td>
                <Td>{po.submitted_by_name || <Muted>--</Muted>}</Td>
                <Td align="center">
                  <Button size="sm" variant="ghost" onClick={() => onOpenPO(po.id)}>
                    View
                  </Button>
                </Td>
              </TRow>
            ))}
          </tbody>
        </Table>
      )}
    </PageContainer>
  );
}

// ============================================================
// DETAIL VIEW
// ============================================================

function PODetailView({
  poId,
  onBack,
  isManager,
}: {
  poId: string;
  onBack: () => void;
  isManager: boolean;
}) {
  const [po, setPo] = useState<PO | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Receive mode state
  const [showReceive, setShowReceive] = useState(false);
  const [receiveData, setReceiveData] = useState<Record<string, {
    invoice_cost?: number;
    qty_received: number;
    qty_damaged: number;
    substitution_note: string;
  }>>({});
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [sendNote, setSendNote] = useState<string | null>(null);
  // What the receiver wrote at the door, and the money on the invoice that is
  // not product. Kept as strings so a half-typed "1." survives.
  const [receivingNote, setReceivingNote] = useState("");
  const [deliveryFee, setDeliveryFee] = useState("");
  const [kegCharged, setKegCharged] = useState("");
  const [kegReturned, setKegReturned] = useState("");
  const [miscLines, setMiscLines] = useState<Array<{ label: string; amount: string }>>([]);
  // PLCB orders: the licensee invoice, box for box, so its sales tax can be
  // claimed back on the PA-3. Used in the receive form and, after receiving,
  // in its own card until the claim has gone on a filed return.
  const [plcbForm, setPlcbForm] = useState<PlcbInvoiceForm>(blankPlcbForm());
  const [plcbStatus, setPlcbStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [savingPlcb, setSavingPlcb] = useState(false);

  // Product search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTab, setSearchTab] = useState<string>("sold_recently");
  const [suggestionQtys, setSuggestionQtys] = useState<Record<string, number>>({});

  // Expected delivery date for submit
  const [expectedDelivery, setExpectedDelivery] = useState("");

  // PDF download
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Email to rep
  const [emailing, setEmailing] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);

  // Local keystroke buffer for the Cases / Case-cost inputs. Values commit to
  // the API on blur or Enter, NOT per keystroke — a per-keystroke save
  // triggered loadPO()'s full-page spinner, unmounting the input mid-word.
  const [lineEdits, setLineEdits] = useState<Record<string, { cases?: string; caseCost?: string }>>({});

  useEffect(() => {
    loadPO();
  }, [poId]);

  const loadPO = async (initial = false) => {
    try {
      if (initial || !po) setLoading(true);
      const response = await api.get(`/purchase-orders/${poId}`);
      setPo(response.data);
      if (response.data.vendor_name === "PLCB") {
        setPlcbForm(plcbFormFrom(response.data.plcb_invoice, response.data.expected_delivery_date || undefined));
      }

      // Load suggestions for editable POs
      if (response.data.status !== "received") {
        try {
          const sugRes = await api.get(`/purchase-orders/suggested/${response.data.vendor_id}`);
          setSuggestions(sugRes.data);
        } catch {
          // Suggestions are optional
        }
      }
    } catch {
      setError("Could not load purchase order.");
    } finally {
      setLoading(false);
    }
  };

  // ── Line actions ──

  const addLine = async (suggestion: Suggestion, qty?: number) => {
    const casesToOrder = qty ?? suggestionQtys[suggestion.product_id] ?? (suggestion.suggested_qty || 1);
    try {
      // Lines are entered in cases and $/case; the API derives receiving units.
      await api.post(`/purchase-orders/${poId}/lines`, {
        product_id: suggestion.product_id,
        receive_format_id: suggestion.receive_format_id,
        cases: casesToOrder,
        cost_per_case: Number(suggestion.default_cost) || 0,
      });
      setSuggestionQtys((prev) => { const next = { ...prev }; delete next[suggestion.product_id]; return next; });
      loadPO();
    } catch {
      setError("Failed to add line item.");
    }
  };

  const updateLine = async (lineId: string, field: string, value: number) => {
    try {
      await api.put(`/purchase-orders/lines/${lineId}`, { [field]: value });
      loadPO();
    } catch {
      setError("Failed to update line item.");
    }
  };

  const removeLine = async (lineId: string) => {
    try {
      await api.delete(`/purchase-orders/lines/${lineId}`);
      loadPO();
    } catch {
      setError("Failed to remove line item.");
    }
  };

  // ── Submit ──

  // Submitting an order and telling the vendor about it are two decisions, so
  // they are two buttons. Patrick, 22 Sep: an order worked up from an invoice
  // already delivered should not go out as though it were new.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deletePO = async () => {
    setDeleting(true);
    try {
      await api.delete(`/purchase-orders/${poId}`);
      onBack();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || "Could not delete this order.");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const submitPO = async (sendToVendor: boolean) => {
    try {
      const res = await api.post(`/purchase-orders/${poId}/submit`, {
        expected_delivery_date: expectedDelivery || undefined,
        send_to_vendor: sendToVendor,
      });
      const detail = (res?.data as { send?: { detail?: string } } | undefined)?.send?.detail;
      setSendNote(detail || (sendToVendor ? "Submitted." : "Submitted. The vendor was not emailed."));
      loadPO();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Failed to submit PO";
      setError(msg);
    }
  };

  // ── what the delivery comes to ──
  //
  // The invoice prints a line TOTAL against a case count; Karen holds a
  // per-selling-unit cost. Typing the printed total and deriving the cost is
  // the right way round: it ties by construction, and it stops anyone
  // hand-dividing 337.50 by 405 at the back door.
  const lineTotalFor = (l: POLine): number => {
    const d = receiveData[l.id];
    const qty = Number(d?.qty_received ?? l.qty_ordered) || 0;
    const cost = Number(d?.invoice_cost ?? l.unit_cost) || 0;
    return Math.round(qty * cost * 100) / 100;
  };

  const setLineTotal = (l: POLine, total: number) => {
    const d = receiveData[l.id];
    const qty = Number(d?.qty_received ?? l.qty_ordered) || 0;
    if (qty <= 0) return;
    // Four decimals on the unit cost: rounding it to the cent is exactly what
    // makes the line total drift off the invoice.
    setReceiveData({ ...receiveData, [l.id]: { ...d, invoice_cost: Math.round((total / qty) * 1e4) / 1e4 } });
  };

  const biggestLine = (): POLine | null => {
    let top: POLine | null = null, topTotal = 0;
    for (const l of po?.lines || []) {
      const t = lineTotalFor(l);
      if (t > topTotal) { topTotal = t; top = l; }
    }
    return top;
  };

  const absorbDifference = () => {
    const l = biggestLine();
    if (!l || paperTotal === null) return;
    const diff = Math.round((paperTotal - karenTotal) * 100) / 100;
    if (Math.abs(diff) > 1) return;   // anything bigger is a real error
    setLineTotal(l, Math.round((lineTotalFor(l) + diff) * 100) / 100);
  };

  /** A money input as a number. Blank, "-" and "1." all read as 0, not NaN. */
  const money = (v: string): number => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  };
  const usd = (n: number) => (n < 0 ? "-$" : "$") + Math.abs(n).toFixed(2);

  const isPlcb = po?.vendor_name === "PLCB";
  const plcb = plcbMath(plcbForm);
  const productTotal = (po?.lines || []).reduce((sum, l) => sum + lineTotalFor(l), 0);
  // PLCB prints each line at the gross price and takes the licensee discount
  // once, at the bottom. Lines typed as printed total the gross; Karen spreads
  // the discount over them when the order is received.
  const plcbGross = money(plcbForm.gross_amount);
  const plcbDiscountOnLines = isPlcb && plcbGross > 0 && money(plcbForm.licensee_discount) > 0 &&
    Math.abs(productTotal - plcbGross) <= 0.01;
  const adjustmentTotal =
    money(deliveryFee) + money(kegCharged) - money(kegReturned) +
    miscLines.reduce((t, m) => t + money(m.amount), 0) +
    (isPlcb ? plcb.tax : 0) -
    (plcbDiscountOnLines ? money(plcbForm.licensee_discount) : 0);
  const karenTotal = Math.round((productTotal + adjustmentTotal) * 100) / 100;
  // On a PLCB order the amount paid is the bottom line of the PLCB invoice.
  const paperTotal = isPlcb
    ? (plcbForm.total_paid.trim() === "" ? null : plcb.paid)
    : invoiceAmount.trim() === "" ? null : money(invoiceAmount);
  const mismatch = paperTotal !== null && Math.abs(paperTotal - karenTotal) > 0.004;

  // ── Receive ──

  const startReceive = () => {
    if (!po) return;
    const data: typeof receiveData = {};
    for (const line of po.lines) {
      data[line.id] = {
        qty_received: line.qty_ordered,
        qty_damaged: 0,
        substitution_note: "",
        invoice_cost: Number(line.unit_cost),
      };
    }
    setReceiveData(data);
    if (po.vendor_name === "PLCB") setPlcbForm(plcbFormFrom(po.plcb_invoice, po.expected_delivery_date || undefined));
    setShowReceive(true);
  };

  const savePlcb = async () => {
    if (!po) return;
    setSavingPlcb(true);
    setPlcbStatus(null);
    try {
      const res = await api.put(`/purchase-orders/${poId}/plcb-invoice`, plcbPayload(plcbForm));
      const warnings: string[] = res.data.warnings || [];
      setPlcbStatus({ ok: true, text: ["Saved. The sales tax is on the next sales tax return as a credit.", ...warnings].join(" ") });
      loadPO();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Could not save the PLCB invoice";
      setPlcbStatus({ ok: false, text: msg });
    } finally {
      setSavingPlcb(false);
    }
  };

  const confirmReceive = async () => {
    if (!po) return;
    const lines = po.lines.map((line) => ({
      line_id: line.id,
      qty_received: receiveData[line.id]?.qty_received ?? line.qty_ordered,
      qty_damaged: receiveData[line.id]?.qty_damaged ?? 0,
      substitution_note: receiveData[line.id]?.substitution_note || undefined,
    }));

    if (isPlcb && !plcb.ties) {
      setError("Fill in the PLCB invoice so it adds up (gross - discount + tax = total paid) before receiving. The sales tax on it is claimed back on the sales tax return.");
      return;
    }

    try {
      const res = await api.post(`/purchase-orders/${poId}/receive`, {
        lines,
        invoice_amount: isPlcb ? plcb.paid : invoiceAmount ? Number(invoiceAmount) : undefined,
        plcb_invoice: isPlcb ? plcbPayload(plcbForm) : undefined,
        receiving_note: receivingNote.trim() || undefined,
        delivery_fee: money(deliveryFee),
        keg_deposit_charged: money(kegCharged),
        keg_deposit_returned: money(kegReturned),
        charges: miscLines
          .filter((m) => m.label.trim() && money(m.amount) !== 0)
          .map((m) => ({ label: m.label.trim(), amount: money(m.amount) })),
      });
      setShowReceive(false);
      const warnings: string[] = res.data?.warnings || [];
      if (warnings.length) setError(warnings.join(" "));
      loadPO();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Receive failed";
      setError(msg);
    }
  };

  // ── PDF ──

  const downloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await api.get(`/purchase-orders/${poId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `PO-${poId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const emailToRep = async () => {
    if (!po?.sales_rep_email) return;
    try {
      setEmailing(true);
      setEmailStatus(null);
      await api.post(`/purchase-orders/${poId}/email-rep`, {
        email: po.sales_rep_email,
      });
      setEmailStatus(`Sent to ${po.sales_rep_name || po.sales_rep_email}`);
    } catch {
      setError("Failed to email PO to sales rep.");
    } finally {
      setEmailing(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Purchase Order" subtitle="Loading..." />
      </PageContainer>
    );
  }

  if (error && !po) {
    return (
      <PageContainer>
        <PageHeader title="Purchase Order" />
        <AlertBanner variant="danger">{error}</AlertBanner>
        <Button onClick={onBack}>Back to List</Button>
      </PageContainer>
    );
  }

  if (!po) return null;

  const addedProductIds = new Set(po.lines.map((l) => l.product_id));
  const availableSuggestions = suggestions.filter((s) => !addedProductIds.has(s.product_id));
  const filteredSuggestions = searchQuery
    ? availableSuggestions.filter(
        (s) =>
          s.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.brand?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : availableSuggestions;

  const belowParSuggestions = filteredSuggestions.filter((s) => s.below_par);
  const soldRecentlySuggestions = filteredSuggestions.filter((s) => s.sold_2wk > 0);
  const allSuggestions = filteredSuggestions;

  const displaySuggestions = searchTab === "below_par"
    ? belowParSuggestions
    : searchTab === "sold_recently"
    ? soldRecentlySuggestions
    : allSuggestions;

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium mb-3 transition-colors"
          style={{ color: "var(--rbw-gray-dark)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
        >
          &larr; Back to Purchase Orders
        </button>
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: "var(--rbw-navy)" }}>
              PO: {po.vendor_name}
            </h1>
            <p className="mt-1 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
              Created {fmtDate(po.created_at)}
              {po.submitted_at && ` | Submitted ${fmtDate(po.submitted_at)}`}
              {po.received_at && ` | Received ${fmtDate(po.received_at)}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={STATUS_BADGE[po.status] || "default"}>{po.status}</Badge>

            {po.status !== "draft" && (
              <Button size="sm" variant="ghost" onClick={downloadPdf} disabled={downloadingPdf}>
                {downloadingPdf ? "..." : "PDF"}
              </Button>
            )}

            {po.status !== "draft" && po.sales_rep_email && isManager && (
              <Button size="sm" variant="ghost" onClick={emailToRep} disabled={emailing}>
                {emailing ? "Sending..." : `Email ${po.sales_rep_name || "Rep"}`}
              </Button>
            )}

            {po.status === "draft" && isManager && (
              <Button variant="ghost" onClick={onBack}>
                Save Draft
              </Button>
            )}
            {po.status !== "received" && isManager && (
              <>
                <Button onClick={() => submitPO(false)} disabled={po.lines.length === 0}>
                  Submit without sending
                </Button>
                <Button onClick={() => submitPO(true)} disabled={po.lines.length === 0}>
                  Submit and email {po.vendor_name}
                </Button>
              </>
            )}
            {po.status === "submitted" && isManager && (
              <Button onClick={startReceive}>
                Receive Delivery
              </Button>
            )}
            {po.status !== "received" && isManager && (
              confirmDelete ? (
                <>
                  <span className="text-sm" style={{ color: "var(--rbw-red)" }}>
                    Delete this {po.status} order for {po.vendor_name}? It has not touched stock.
                  </span>
                  <Button size="sm" variant="danger" onClick={deletePO} disabled={deleting}>
                    {deleting ? "Deleting..." : "Yes, delete it"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                    Keep it
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                  Delete order
                </Button>
              )
            )}
          </div>
        </div>
      </div>

      {sendNote && <AlertBanner variant="gold"><div style={{ color: "var(--rbw-navy)" }}>{sendNote}</div></AlertBanner>}

      {po.status !== "draft" && po.send_outcome && po.send_outcome !== "sent" && (
        <AlertBanner variant="gold">
          <div style={{ color: "var(--rbw-navy)" }}>
            <strong>The vendor was not emailed this order.</strong>{" "}
            {po.send_outcome === "not_requested"
              ? "It was submitted without sending, which is what was asked for."
              : po.send_outcome === "no_address"
              ? `${po.vendor_name} has no order email in Karen.`
              : po.send_outcome === "not_configured"
              ? "Karen has no mail server set up, so it could not be sent."
              : "The send failed. If the vendor needs it, send it yourself."}
          </div>
        </AlertBanner>
      )}

      {!showReceive && po.receiving_note && (
        <AlertBanner variant="gold">
          <div>
            <div className="text-sm font-semibold mb-1" style={{ color: "var(--rbw-navy)" }}>Delivery note</div>
            <div style={{ color: "var(--rbw-navy)", whiteSpace: "pre-wrap" }}>{po.receiving_note}</div>
          </div>
        </AlertBanner>
      )}

      {error && <AlertBanner variant="danger">{error}</AlertBanner>}
      {emailStatus && <AlertBanner variant="green">{emailStatus}</AlertBanner>}

      {/* Notes */}
      {po.notes && (
        <AlertBanner variant="blue">
          <strong>Notes:</strong> {po.notes}
        </AlertBanner>
      )}

      {/* Summary stats for this PO */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Line Items"
          value={String(po.lines.length)}
          variant="default"
        />
        <StatCard
          label="PO Total"
          value={fmt(Number(po.total_amount))}
          variant="gold"
        />
        <StatCard
          label="Invoice Amount"
          value={po.invoice_amount ? fmt(Number(po.invoice_amount)) : "--"}
          variant={po.invoice_amount ? "blue" : "default"}
        />
        <StatCard
          label="Status"
          value={po.status.charAt(0).toUpperCase() + po.status.slice(1)}
          variant={po.status === "received" ? "green" : po.status === "submitted" ? "blue" : "default"}
        />
      </div>

      {/* Expected delivery date for submit */}
      {po.status !== "received" && isManager && (
        <Card className="mb-6" padding="p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
              Expected Delivery Date:
            </label>
            <input
              type="date"
              value={expectedDelivery}
              onChange={(e) => setExpectedDelivery(e.target.value)}
              className="px-4 py-2 rounded-xl outline-none"
              style={{
                border: "2px solid var(--rbw-border)",
                background: "var(--rbw-bg-card)",
                color: "var(--rbw-navy)",
              }}
            />
          </div>
        </Card>
      )}

      {/* PO Lines Table */}
      <Card className="mb-6" padding="p-0">
        <div className="px-6 pt-6 pb-4" style={{ borderBottom: "1px solid var(--rbw-border)" }}>
          <h2 className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
            Order Lines
          </h2>
        </div>

        {po.lines.length === 0 ? (
          <div className="p-6">
            <EmptyState message="No line items yet" detail="Add products from the suggestions below" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: "var(--rbw-navy)" }}>
                  <Th>Product</Th>
                  <Th align="center">Cases</Th>
                  <Th align="right">Case Cost</Th>
                  {showReceive && <Th align="center">Received</Th>}
                  {showReceive && <Th align="center">Damaged</Th>}
                  {showReceive && <Th align="right">Cost each</Th>}
                  {showReceive && <Th align="right">Line total</Th>}
                  {showReceive && <Th>Sub. Note</Th>}
                  {po.status === "received" && <Th align="center">Received</Th>}
                  {po.status === "received" && <Th align="center">Damaged</Th>}
                  <Th align="right">Line Total</Th>
                  {po.status !== "received" && isManager && <Th align="center"> </Th>}
                </tr>
              </thead>
              <tbody>
                {po.lines.map((line, idx) => (
                  <TRow key={line.id} index={idx}>
                    <Td>
                      <div className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                        {line.product_name}
                      </div>
                      <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                        {line.brand && `${line.brand} | `}{line.case_configuration || line.receive_format_name}
                        {" | "}On hand: {Number(line.cases_on_hand)}
                        {" | "}Sold 2wk: {Number(line.sold_2wk_cases)}
                      </div>
                    </Td>

                    {/* Cases - editable in draft */}
                    <Td align="center">
                      {po.status !== "received" && isManager ? (
                        <input
                          type="number"
                          value={lineEdits[line.id]?.cases ?? String(Number(line.cases_ordered))}
                          onChange={(e) => {
                            const cases = e.target.value;
                            setLineEdits((prev) => ({ ...prev, [line.id]: { ...prev[line.id], cases } }));
                          }}
                          onBlur={(e) => {
                            const cases = Number(e.target.value);
                            setLineEdits((prev) => {
                              const next = { ...prev };
                              if (next[line.id]) { const { cases: _c, ...rest } = next[line.id]; next[line.id] = rest; }
                              return next;
                            });
                            if (!Number.isFinite(cases) || cases < 1 || cases === Number(line.cases_ordered)) return;
                            updateLine(line.id, "cases", cases);
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
                          className="w-20 px-2 py-1 rounded-lg text-center outline-none"
                          style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
                          min="1"
                        />
                      ) : (
                        <span>{Number(line.cases_ordered)}</span>
                      )}
                    </Td>

                    {/* Case cost - editable in draft */}
                    <Td align="right">
                      {po.status !== "received" && isManager ? (
                        <input
                          type="number"
                          step="0.01"
                          value={lineEdits[line.id]?.caseCost ?? String(Number(line.cost_per_case))}
                          onChange={(e) => {
                            const caseCost = e.target.value;
                            setLineEdits((prev) => ({ ...prev, [line.id]: { ...prev[line.id], caseCost } }));
                          }}
                          onBlur={(e) => {
                            const caseCost = Number(e.target.value);
                            setLineEdits((prev) => {
                              const next = { ...prev };
                              if (next[line.id]) { const { caseCost: _c, ...rest } = next[line.id]; next[line.id] = rest; }
                              return next;
                            });
                            if (!Number.isFinite(caseCost) || caseCost < 0 || caseCost === Number(line.cost_per_case)) return;
                            updateLine(line.id, "cost_per_case", caseCost);
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
                          className="w-24 px-2 py-1 rounded-lg text-right outline-none"
                          style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
                        />
                      ) : (
                        <span>{fmt(Number(line.cost_per_case))}</span>
                      )}
                    </Td>

                    {/* Receive mode inputs */}
                    {showReceive && (
                      <>
                        <Td align="center">
                          <input
                            type="number"
                            value={receiveData[line.id]?.qty_received ?? line.qty_ordered}
                            onChange={(e) =>
                              setReceiveData({
                                ...receiveData,
                                [line.id]: {
                                  ...receiveData[line.id],
                                  qty_received: Number(e.target.value),
                                },
                              })
                            }
                            className="w-20 px-2 py-1 rounded-lg text-center outline-none"
                            style={{
                              border: `2px solid ${
                                (receiveData[line.id]?.qty_received ?? line.qty_ordered) !== line.qty_ordered
                                  ? "var(--rbw-gold)"
                                  : "var(--rbw-border)"
                              }`,
                              color: "var(--rbw-navy)",
                              background:
                                (receiveData[line.id]?.qty_received ?? line.qty_ordered) !== line.qty_ordered
                                  ? "#FFFBEB"
                                  : "white",
                            }}
                            min="0"
                          />
                        </Td>
                        <Td align="center">
                          <input
                            type="number"
                            value={receiveData[line.id]?.qty_damaged ?? 0}
                            onChange={(e) =>
                              setReceiveData({
                                ...receiveData,
                                [line.id]: {
                                  ...receiveData[line.id],
                                  qty_damaged: Number(e.target.value),
                                },
                              })
                            }
                            className="w-20 px-2 py-1 rounded-lg text-center outline-none"
                            style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
                            min="0"
                          />
                        </Td>
                        <Td align="right">
                          <input type="number" step="0.0001"
                            value={receiveData[line.id]?.invoice_cost ?? Number(line.unit_cost)}
                            onChange={(e) =>
                              setReceiveData({
                                ...receiveData,
                                [line.id]: { ...receiveData[line.id], invoice_cost: Number(e.target.value) },
                              })
                            }
                            className="w-24 px-2 py-1 rounded-lg text-right outline-none"
                            style={{
                              border: `2px solid ${(receiveData[line.id]?.invoice_cost ?? Number(line.unit_cost)) !== Number(line.unit_cost) ? "var(--rbw-gold)" : "var(--rbw-border)"}`,
                              color: "var(--rbw-navy)",
                              background: (receiveData[line.id]?.invoice_cost ?? Number(line.unit_cost)) !== Number(line.unit_cost) ? "#FFFBEB" : "white",
                            }} />
                        </Td>
                        {/* Type what the invoice says this line came to; Karen
                            works the unit cost backwards so it ties exactly. */}
                        <Td align="right">
                          <input type="number" step="0.01"
                            value={lineTotalFor(line)}
                            onChange={(e) => setLineTotal(line, Number(e.target.value))}
                            className="w-24 px-2 py-1 rounded-lg text-right outline-none"
                            style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)",
                                     background: "white", fontWeight: 600 }} />
                        </Td>
                        <Td>
                          <input
                            type="text"
                            placeholder="Substitution note..."
                            value={receiveData[line.id]?.substitution_note ?? ""}
                            onChange={(e) =>
                              setReceiveData({
                                ...receiveData,
                                [line.id]: {
                                  ...receiveData[line.id],
                                  substitution_note: e.target.value,
                                },
                              })
                            }
                            className="w-40 px-2 py-1 rounded-lg outline-none text-sm"
                            style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
                          />
                        </Td>
                      </>
                    )}

                    {/* Received status columns */}
                    {po.status === "received" && (
                      <>
                        <Td align="center">
                          <span
                            style={{
                              color:
                                line.qty_received !== line.qty_ordered
                                  ? "var(--rbw-danger)"
                                  : "var(--rbw-navy)",
                              fontWeight: line.qty_received !== line.qty_ordered ? 700 : 400,
                            }}
                          >
                            {line.qty_received ?? "--"}
                          </span>
                        </Td>
                        <Td align="center">
                          <span
                            style={{
                              color: line.qty_damaged > 0 ? "var(--rbw-danger)" : "var(--rbw-navy)",
                              fontWeight: line.qty_damaged > 0 ? 700 : 400,
                            }}
                          >
                            {line.qty_damaged || 0}
                          </span>
                        </Td>
                      </>
                    )}

                    <Td align="right" bold>
                      {fmt(Number(line.line_total))}
                    </Td>

                    {/* Remove button for draft */}
                    {po.status !== "received" && isManager && (
                      <Td align="center">
                        <Button size="sm" variant="danger" onClick={() => removeLine(line.id)}>
                          Remove
                        </Button>
                      </Td>
                    )}
                  </TRow>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "var(--rbw-bg)", borderTop: "2px solid var(--rbw-border)" }}>
                  <td className="px-5 py-4 text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
                    Total
                  </td>
                  <td className="px-5 py-4 text-sm text-center font-bold" style={{ color: "var(--rbw-navy)" }}>
                    {Math.round(po.lines.reduce((sum, l) => sum + Number(l.cases_ordered), 0) * 10) / 10}
                  </td>
                  <td className="px-5 py-4" />
                  {showReceive && (
                    <>
                      <td className="px-5 py-4" />
                      <td className="px-5 py-4" />
                      <td className="px-5 py-4" />
                    </>
                  )}
                  {po.status === "received" && (
                    <>
                      <td className="px-5 py-4 text-sm text-center font-bold" style={{ color: "var(--rbw-navy)" }}>
                        {po.lines.reduce((sum, l) => sum + (l.qty_received ?? 0), 0)}
                      </td>
                      <td className="px-5 py-4" />
                    </>
                  )}
                  <td className="px-5 py-4 text-sm text-right font-bold" style={{ color: "var(--rbw-navy)" }}>
                    {fmt(Number(po.total_amount))}
                  </td>
                  {po.status !== "received" && isManager && <td className="px-5 py-4" />}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Receive action bar */}
      {showReceive && (
        <AlertBanner variant="gold">
          <div className="flex items-center gap-4 flex-wrap">
            {/* The money on the invoice that is not product: the delivery
                surcharge, keg deposits charged, empties credited back, and
                anything else the vendor put on the paper. Entering them here
                means Karen's total ties to the invoice without anyone doing
                the arithmetic in their head. */}
            <div style={{ minWidth: "330px" }}>
              {isPlcb && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
                    PLCB invoice <span style={{ fontWeight: 400, color: "var(--rbw-gray-dark)" }}>(copy it exactly as printed)</span>
                  </label>
                  <PlcbInvoiceFields value={plcbForm} onChange={setPlcbForm} />
                </div>
              )}
              <label className="block text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
                Invoice total
              </label>
              <table style={{ borderCollapse: "collapse", width: "100%", fontVariantNumeric: "tabular-nums" }}>
                <tbody>
                  <tr>
                    <td style={{ padding: "4px 12px 4px 0", color: "var(--rbw-navy)" }}>Product on this invoice</td>
                    <td style={{ padding: "4px 0", textAlign: "right", fontWeight: 600, color: "var(--rbw-navy)" }}>
                      {usd(productTotal)}
                    </td>
                  </tr>
                  {plcbDiscountOnLines && (
                    <tr>
                      <td style={{ padding: "4px 12px 4px 0", color: "var(--rbw-navy)" }}>- Licensee discount (Karen spreads it over the lines)</td>
                      <td style={{ padding: "4px 0", textAlign: "right", fontWeight: 600, color: "var(--rbw-navy)" }}>{usd(-money(plcbForm.licensee_discount))}</td>
                    </tr>
                  )}
                  {isPlcb && (
                    <tr>
                      <td style={{ padding: "4px 12px 4px 0", color: "var(--rbw-navy)" }}>+ PA sales tax (from the PLCB invoice)</td>
                      <td style={{ padding: "4px 0", textAlign: "right", fontWeight: 600, color: "var(--rbw-navy)" }}>{usd(plcb.tax)}</td>
                    </tr>
                  )}
                  {!isPlcb && ([
                    ["+ Delivery fee", deliveryFee, setDeliveryFee],
                    ["+ Keg deposits charged", kegCharged, setKegCharged],
                    ["- Keg returns / credits", kegReturned, setKegReturned],
                  ] as Array<[string, string, (v: string) => void]>).map(([label, val, set]) => (
                    <tr key={label}>
                      <td style={{ padding: "4px 12px 4px 0", color: "var(--rbw-navy)" }}>{label}</td>
                      <td style={{ padding: "4px 0", textAlign: "right" }}>
                        <input type="number" step="0.01" value={val} onChange={(e) => set(e.target.value)}
                          className="px-3 py-1.5 rounded-lg outline-none"
                          style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)",
                                   background: "var(--rbw-bg-card)", width: "110px", textAlign: "right" }}
                          placeholder="0.00" />
                      </td>
                    </tr>
                  ))}
                  {miscLines.map((m, i) => (
                    <tr key={i}>
                      <td style={{ padding: "4px 12px 4px 0" }}>
                        <input value={m.label}
                          onChange={(e) => setMiscLines(miscLines.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                          className="px-3 py-1.5 rounded-lg outline-none"
                          style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)",
                                   background: "var(--rbw-bg-card)", width: "100%" }}
                          placeholder="What was it for?" />
                      </td>
                      <td style={{ padding: "4px 0", textAlign: "right", whiteSpace: "nowrap" }}>
                        <input type="number" step="0.01" value={m.amount}
                          onChange={(e) => setMiscLines(miscLines.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                          className="px-3 py-1.5 rounded-lg outline-none"
                          style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)",
                                   background: "var(--rbw-bg-card)", width: "110px", textAlign: "right" }}
                          placeholder="0.00" />
                        <button type="button" onClick={() => setMiscLines(miscLines.filter((_, j) => j !== i))}
                          style={{ marginLeft: 6, background: "none", border: "none", cursor: "pointer",
                                   color: "var(--rbw-gray-dark)", fontSize: "1.1em" }}
                          title="Remove this charge">&times;</button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={2} style={{ padding: "2px 0 6px" }}>
                      <button type="button" onClick={() => setMiscLines([...miscLines, { label: "", amount: "" }])}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                                 color: "var(--rbw-navy)", textDecoration: "underline", fontWeight: 600, font: "inherit" }}>
                        + add another charge
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "8px 12px 4px 0", borderTop: "2px solid var(--rbw-border)", fontWeight: 700, color: "var(--rbw-navy)" }}>
                      Karen makes it
                    </td>
                    <td style={{ padding: "8px 0 4px", borderTop: "2px solid var(--rbw-border)", textAlign: "right", fontWeight: 700, fontSize: "1.1em", color: "var(--rbw-navy)" }}>
                      {usd(karenTotal)}
                    </td>
                  </tr>
                  {!isPlcb && <tr>
                    <td style={{ padding: "4px 12px 4px 0", color: "var(--rbw-navy)" }}>Amount due on the paper</td>
                    <td style={{ padding: "4px 0", textAlign: "right" }}>
                      <input type="number" step="0.01" value={invoiceAmount}
                        onChange={(e) => setInvoiceAmount(e.target.value)}
                        className="px-3 py-1.5 rounded-lg outline-none"
                        style={{ border: mismatch ? "2px solid #C53030" : "2px solid var(--rbw-border)",
                                 color: "var(--rbw-navy)", background: "var(--rbw-bg-card)",
                                 width: "110px", textAlign: "right" }}
                        placeholder="0.00" />
                    </td>
                  </tr>}
                </tbody>
              </table>
              {mismatch && paperTotal !== null && (
                <div className="text-sm mt-2" style={{ color: "#C53030", fontWeight: 600 }}>
                  Off by {usd(Math.round((paperTotal - karenTotal) * 100) / 100)}.
                  {Math.abs(paperTotal - karenTotal) <= 1 && biggestLine() && (
                    <>
                      {" "}
                      <button type="button" onClick={absorbDifference}
                        style={{ textDecoration: "underline", fontWeight: 600, color: "#C53030",
                                 background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}>
                        Put it on {biggestLine()!.product_name}
                      </button>
                      {" to make it tie."}
                    </>
                  )}
                </div>
              )}
              {!mismatch && paperTotal !== null && (
                <div className="text-sm mt-2" style={{ color: "#2D8A4E", fontWeight: 600 }}>Matches the invoice.</div>
              )}
            </div>

            <div className="flex-1" style={{ minWidth: "240px" }}>
              <label className="block text-sm font-semibold mb-1" style={{ color: "var(--rbw-navy)" }}>
                Notes about this delivery{" "}
                <span style={{ fontWeight: 400, color: "var(--rbw-gray-dark)" }}>(optional)</span>
              </label>
              <textarea value={receivingNote} rows={3}
                onChange={(e) => setReceivingNote(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl outline-none"
                style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)",
                         background: "var(--rbw-bg-card)", resize: "vertical" }}
                placeholder="Anything about the delivery as a whole - driver, damage, what the rep said" />
            </div>
            <div className="flex gap-2">
              <Button onClick={confirmReceive}>Confirm Receive</Button>
              <Button variant="ghost" onClick={() => setShowReceive(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </AlertBanner>
      )}

      {/* Suggestions for draft PO */}
      {po.status !== "received" && isManager && availableSuggestions.length > 0 && (
        <Card padding="p-0">
          <div className="px-6 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--rbw-border)" }}>
            <h2 className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
              Add Products
            </h2>
            <div className="flex items-center gap-3">
              <TabGroup
                tabs={[
                  { key: "sold_recently", label: `Sold 2wk (${soldRecentlySuggestions.length})` },
                  { key: "below_par", label: `Below Par (${belowParSuggestions.length})` },
                  { key: "suggested", label: `All (${allSuggestions.length})` },
                ]}
                active={searchTab}
                onChange={setSearchTab}
              />
            </div>
          </div>

          {/* Search */}
          <div className="px-6 py-3" style={{ borderBottom: "1px solid var(--rbw-border)" }}>
            <Input
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search products by name or brand..."
            />
          </div>

          {/* Product list */}
          <div className="max-h-96 overflow-y-auto">
            {displaySuggestions.length === 0 ? (
              <div className="p-6 text-center">
                <Muted>No matching products found</Muted>
              </div>
            ) : (
              displaySuggestions.map((s, idx) => (
                <div
                  key={s.product_id}
                  className="flex items-center justify-between px-6 py-3"
                  style={{
                    borderBottom: "1px solid var(--rbw-border)",
                    background: s.below_par
                      ? "#C5303008"
                      : idx % 2 === 0
                      ? "transparent"
                      : "var(--rbw-bg)",
                  }}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm" style={{ color: "var(--rbw-navy)" }}>
                        {s.product_name}
                      </span>
                      {s.case_sku && (
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: "var(--rbw-bg)", color: "var(--rbw-gray-dark)", border: "1px solid var(--rbw-border)" }}>
                          #{s.case_sku}
                        </span>
                      )}
                      {s.below_par && <Badge variant="danger">BELOW PAR</Badge>}
                      {s.wholesaler_oos_since && (
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: "#92400E" }}
                          title="Marked out of stock at the wholesaler — keep trying on each order">
                          OOS AT WHOLESALER since {new Date(s.wholesaler_oos_since + "T12:00:00").toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--rbw-gray-dark)" }}>
                      {s.brand && `${s.brand} | `}
                      {s.case_configuration || s.receive_format_name}
                      {" | "}On hand: {s.cases_on_hand < 0 ? "needs count" : `${s.cases_on_hand} cases`}
                      {" | "}Sold 2wk: {s.sold_2wk_cases} cases
                      {s.days_of_stock < 999 && ` | ${s.days_of_stock}d stock`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    {s.suggested_qty > 0 && (
                      <span className="text-xs font-medium px-2 py-1 rounded" style={{ background: "var(--rbw-bg)", color: "var(--rbw-navy)" }}>
                        Suggest: {s.suggested_qty} case{s.suggested_qty === 1 ? "" : "s"}
                      </span>
                    )}
                    <span className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                      {fmt(Number(s.default_cost))}/case
                    </span>
                    <div className="flex flex-col items-center">
                      <input
                        type="number"
                        min="1"
                        value={suggestionQtys[s.product_id] ?? (s.suggested_qty || 1)}
                        onChange={(e) => setSuggestionQtys((prev) => ({ ...prev, [s.product_id]: Number(e.target.value) }))}
                        className="w-14 px-2 py-1 rounded-lg text-center text-sm outline-none"
                        style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
                      />
                      <span className="text-[10px]" style={{ color: "var(--rbw-gray-dark)" }}>cases</span>
                    </div>
                    <Button size="sm" onClick={() => addLine(s)}>
                      Add
                    </Button>
                    <button
                      onClick={async () => {
                        const flag = !s.wholesaler_oos_since;
                        try {
                          await api.post("/purchase-orders/oos-flag", { product_id: s.product_id, oos: flag });
                          setSuggestions((prev) =>
                            prev.map((x) => x.product_id === s.product_id
                              ? { ...x, wholesaler_oos_since: flag ? new Date().toISOString().slice(0, 10) : null }
                              : x)
                          );
                        } catch { /* leave as-is */ }
                      }}
                      title={s.wholesaler_oos_since ? "Wholesaler has it again — clear the flag" : "Tried to order it but the wholesaler is out — flag it so we keep checking"}
                      className="text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap"
                      style={{ background: "var(--rbw-bg)", color: s.wholesaler_oos_since ? "#92400E" : "var(--rbw-gray-dark)", border: "1px solid var(--rbw-border)" }}
                    >
                      {s.wholesaler_oos_since ? "Back in stock" : "OOS?"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      {/* PLCB invoice outside the receive form: fill it in or correct it
          after the fact, until its tax has gone on a filed return. */}
      {isPlcb && !showReceive && po.status !== "draft" && (
        <Card className="mt-6">
          <CardHeader title="PLCB invoice" />
          <div className="px-5 py-4" style={{ maxWidth: 460 }}>
            {!po.plcb_invoice && (
              <p className="text-sm mb-3" style={{ color: "#C53030", fontWeight: 600 }}>
                Not entered yet. Copy the PLCB invoice in so its sales tax can be claimed back.
              </p>
            )}
            {po.plcb_invoice?.claim_status && po.plcb_invoice.claim_status !== "unfiled" ? (
              <>
                <PlcbInvoiceFields value={plcbFormFrom(po.plcb_invoice)} onChange={() => undefined} disabled />
                <p className="text-sm mt-2" style={{ color: "var(--rbw-gray-dark)" }}>
                  The sales tax on this order has been claimed on a filed return ({po.plcb_invoice.claim_status}), so it is locked.
                </p>
              </>
            ) : isManager ? (
              <>
                <PlcbInvoiceFields
                  value={plcbForm}
                  onChange={setPlcbForm} />
                <div className="flex items-center gap-3 mt-3">
                  <Button onClick={savePlcb} disabled={savingPlcb || !plcb.ties}>
                    {savingPlcb ? "Saving..." : "Save PLCB invoice"}
                  </Button>
                </div>
              </>
            ) : (
              <PlcbInvoiceFields value={plcbFormFrom(po.plcb_invoice)} onChange={() => undefined} disabled />
            )}
            {plcbStatus && (
              <p className="text-sm mt-2" style={{ color: plcbStatus.ok ? "#2D8A4E" : "#C53030", fontWeight: 600 }}>{plcbStatus.text}</p>
            )}
          </div>
        </Card>
      )}

      {/* Substitution notes for received */}
      {po.status === "received" && po.lines.some((l) => l.substitution_note) && (
        <Card className="mt-6">
          <CardHeader title="Substitution Notes" />
          {po.lines
            .filter((l) => l.substitution_note)
            .map((line) => (
              <div
                key={line.id}
                className="flex items-start gap-3 px-5 py-3"
                style={{ borderBottom: "1px solid var(--rbw-border)" }}
              >
                <span className="font-medium text-sm" style={{ color: "var(--rbw-navy)" }}>
                  {line.product_name}:
                </span>
                <span className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                  {line.substitution_note}
                </span>
              </div>
            ))}
        </Card>
      )}
    </PageContainer>
  );
}
